<script lang="ts" setup>
import SectionView from '@partner-spa-vue/components/SectionView.vue';
import Badge from '@/components/ui/badge/Badge.vue';
import { currentWebsite } from '@partner-spa-vue/stores/MyWebsitesStore';
import { ChevronRight, HandHelping, Megaphone } from 'lucide-vue-next';

const supportOverview = { name: 'support-earnings', label: 'Overview', description: 'Enable support partner mode and see how it works.' } as const;

const supportSections = [
    { name: 'support-earnings-stripe', label: 'Business and bank details', description: 'Set up your Stripe account to receive payouts.' },
    { name: 'support-earnings-fee-settings', label: 'Fee Settings', description: 'Configure your support partner fee rates.' },
    { name: 'support-earnings-balances', label: 'Balances', description: 'View your current balances.' },
    { name: 'support-earnings-incoming', label: 'Incoming', description: 'View incoming earnings.' },
    { name: 'support-earnings-unpaid', label: 'Unpaid', description: 'View unpaid earnings awaiting payout.' },
    { name: 'support-earnings-history', label: 'History', description: 'View your earnings history.' },
    { name: 'support-earnings-notifications', label: 'Notifications', description: 'Configure notification email settings.' },
    { name: 'support-earnings-imprint', label: 'Imprint', description: 'Configure your imprint information.' },
] as const;

const marketingOverview = { name: 'marketing-earnings', label: 'Overview', description: 'Enable affiliate partner mode and see how it works.' } as const;

const marketingSections = [
    { name: 'marketing-earnings-stripe', label: 'Business and bank details', description: 'Set up your Stripe account to receive payouts.' },
    { name: 'marketing-earnings-fee-settings', label: 'Fee Settings', description: 'Configure your marketing fee rates.' },
    { name: 'marketing-earnings-balances', label: 'Balances', description: 'View your current balances.' },
    { name: 'marketing-earnings-incoming', label: 'Incoming', description: 'View incoming earnings.' },
    { name: 'marketing-earnings-unpaid', label: 'Unpaid', description: 'View unpaid earnings awaiting payout.' },
    { name: 'marketing-earnings-history', label: 'History', description: 'View your earnings history.' },
    { name: 'marketing-earnings-notifications', label: 'Notifications', description: 'Configure notification email settings.' },
] as const;
</script>

<template>
    <SectionView title="Partner website"
                 description="">
        <div class="space-y-4 pb-2">
            <p class="text-sm text-muted-foreground">
                Manage partner earnings for your website. Two ways to earn:
            </p>

            <p v-if="currentWebsite?.defaultHostname"
               class="text-sm text-muted-foreground">
                <a :href="`https://${currentWebsite?.defaultHostname}`"
                   target="_blank"
                   class="text-primary hover:text-primary/80 transition-colors">
                    <Badge class="ml-1">{{ currentWebsite.defaultHostname }}</Badge>
                </a>
                <br>
                <br>
                <a :href="`https://${currentWebsite?.defaultHostname}/dashboard`"
                   target="_blank"
                   class="text-primary hover:text-primary/80 transition-colors">
                    <Badge class="ml-1">/dashboard</Badge>
                </a>
                <br>
                <br>
                <a :href="`https://${currentWebsite?.defaultHostname}/orders`"
                   target="_blank"
                   class="text-primary hover:text-primary/80 transition-colors">
                    <Badge class="ml-1">/orders</Badge>
                </a>
            </p>

            <div class="grid gap-3 sm:grid-cols-2">
                <RouterLink :to="{ name: supportOverview.name }"
                            class="rounded-lg border bg-muted/30 p-4 space-y-3">
                    <div class="flex items-start gap-3">
                        <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                            <HandHelping class="size-5 text-primary" />
                        </div>
                        <div class="space-y-1">
                            <div class="flex flex-wrap items-center gap-2">
                                <h4 class="font-semibold">Support Partner</h4>
                                <Badge :variant="currentWebsite?.isSupportTeamPartner ? 'default' : 'secondary'">
                                    {{ currentWebsite?.isSupportTeamPartner ? 'Enabled' : 'Not enabled' }}
                                </Badge>
                            </div>
                            <p class="text-sm text-muted-foreground">
                                Help stores launch their own ordering website.
                            </p>
                        </div>
                    </div>
                    <p class="text-xs text-muted-foreground border-t pt-3">
                        You earn when their orders come in.
                    </p>
                </RouterLink>

                <RouterLink :to="{ name: marketingOverview.name }"
                            class="rounded-lg border bg-muted/30 p-4 space-y-3">
                    <div class="flex items-start gap-3">
                        <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                            <Megaphone class="size-5 text-primary" />
                        </div>
                        <div class="space-y-1">
                            <div class="flex flex-wrap items-center gap-2">
                                <h4 class="font-semibold">Affiliate Partner</h4>
                                <Badge :variant="currentWebsite?.isMarketingTeamPartner ? 'default' : 'secondary'">
                                    {{ currentWebsite?.isMarketingTeamPartner ? 'Enabled' : 'Not enabled' }}
                                </Badge>
                            </div>
                            <p class="text-sm text-muted-foreground">
                                List stores on your website.
                            </p>
                        </div>
                    </div>
                    <p class="text-xs text-muted-foreground border-t pt-3">
                        You earn when someone orders through your site.
                    </p>
                </RouterLink>
            </div>
        </div>

        <div class="space-y-8">
            <div class="space-y-3">
                <h3 class="text-lg font-semibold">Support Partnership</h3>
                <p class="text-sm text-muted-foreground">
                    Help stores and delivery teams get started with ordering websites. When supported stores
                    receive orders, you earn a support partner fee.
                </p>
                <nav class="border rounded-lg divide-y">
                    <RouterLink :to="{ name: supportOverview.name }"
                                class="flex items-center justify-between gap-4 p-4 hover:bg-muted/50 transition-colors">
                        <div>
                            <div class="font-medium">{{ supportOverview.label }}</div>
                            <div class="text-sm text-muted-foreground">{{ supportOverview.description }}</div>
                        </div>
                        <ChevronRight class="h-4 w-4 shrink-0 text-muted-foreground" />
                    </RouterLink>
                    <template v-if="currentWebsite?.isSupportTeamPartner">
                        <RouterLink v-for="section in supportSections"
                                    :key="section.name"
                                    :to="{ name: section.name }"
                                    class="flex items-center justify-between gap-4 p-4 hover:bg-muted/50 transition-colors">
                            <div>
                                <div class="font-medium">{{ section.label }}</div>
                                <div class="text-sm text-muted-foreground">{{ section.description }}</div>
                            </div>
                            <ChevronRight class="h-4 w-4 shrink-0 text-muted-foreground" />
                        </RouterLink>
                    </template>
                </nav>
                <p v-if="!currentWebsite?.isSupportTeamPartner"
                   class="text-sm text-muted-foreground">
                    Enable support partner on Overview to manage earnings settings.
                </p>
            </div>

            <div class="space-y-3">
                <h3 class="text-lg font-semibold">Affiliate Partnership</h3>
                <p class="text-sm text-muted-foreground">
                    List stores on your website and earn a marketing fee whenever someone places an order
                    through your site.
                </p>
                <nav class="border rounded-lg divide-y">
                    <RouterLink :to="{ name: marketingOverview.name }"
                                class="flex items-center justify-between gap-4 p-4 hover:bg-muted/50 transition-colors">
                        <div>
                            <div class="font-medium">{{ marketingOverview.label }}</div>
                            <div class="text-sm text-muted-foreground">{{ marketingOverview.description }}</div>
                        </div>
                        <ChevronRight class="h-4 w-4 shrink-0 text-muted-foreground" />
                    </RouterLink>
                    <template v-if="currentWebsite?.isMarketingTeamPartner">
                        <RouterLink v-for="section in marketingSections"
                                    :key="section.name"
                                    :to="{ name: section.name }"
                                    class="flex items-center justify-between gap-4 p-4 hover:bg-muted/50 transition-colors">
                            <div>
                                <div class="font-medium">{{ section.label }}</div>
                                <div class="text-sm text-muted-foreground">{{ section.description }}</div>
                            </div>
                            <ChevronRight class="h-4 w-4 shrink-0 text-muted-foreground" />
                        </RouterLink>
                    </template>
                </nav>
                <p v-if="!currentWebsite?.isMarketingTeamPartner"
                   class="text-sm text-muted-foreground">
                    Enable affiliate partner on Overview to manage earnings settings.
                </p>
            </div>
        </div>
    </SectionView>
</template>
